import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { restHost } from '../../apiConfig';
import {
  CheckResult,
  CheckState,
  overallState,
  runStatusChecks,
} from './statusChecks';

const REFRESH_MS = 60_000;

const stateChip = (state: CheckState) => {
  const props: Record<
    CheckState,
    { label: string; color: 'success' | 'warning' | 'error' }
  > = {
    operational: { label: 'OPERATIONAL', color: 'success' },
    degraded: { label: 'DEGRADED', color: 'warning' },
    down: { label: 'DOWN', color: 'error' },
  };
  return <Chip size="small" {...props[state]} data-testid={`chip-${state}`} />;
};

const StatusPage: React.FC = () => {
  const [results, setResults] = useState<CheckResult[] | null>(null);
  const [checkedAt, setCheckedAt] = useState<Date | null>(null);
  const [running, setRunning] = useState(false);

  const refresh = useCallback(async () => {
    setRunning(true);
    const next = await runStatusChecks();
    setResults(next);
    setCheckedAt(new Date());
    setRunning(false);
  }, []);

  useEffect(() => {
    refresh();
    const timer = setInterval(refresh, REFRESH_MS);
    return () => clearInterval(timer);
  }, [refresh]);

  return (
    <Container maxWidth="sm" sx={{ py: 6 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography variant="h5">Ōmcare Backend Status</Typography>
        {results && stateChip(overallState(results))}
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {restHost}
      </Typography>
      {!results ? (
        <Box textAlign="center" py={6}>
          <CircularProgress size={28} />
        </Box>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Check</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Latency</TableCell>
              <TableCell align="right">Detail</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {results.map((r) => (
              <TableRow key={r.name}>
                <TableCell>{r.name}</TableCell>
                <TableCell>{stateChip(r.state)}</TableCell>
                <TableCell
                  align="right"
                  sx={{ fontVariantNumeric: 'tabular-nums' }}
                >
                  {r.latencyMs === null ? '—' : `${r.latencyMs} ms`}
                </TableCell>
                <TableCell align="right">{r.detail}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mt: 3 }}>
        <Button
          variant="outlined"
          size="small"
          onClick={refresh}
          disabled={running}
        >
          Refresh
        </Button>
        <Typography variant="caption" color="text.secondary">
          {checkedAt
            ? `Last checked ${checkedAt.toLocaleTimeString()} · auto-refreshes every 60s`
            : 'Checking…'}
        </Typography>
      </Stack>
    </Container>
  );
};

export default StatusPage;
